import { useEffect } from 'react';

// Custom hook to handle all the home page interactions
const useHomeInteractions = () => {
  useEffect(() => {
    // Feature badges and checkboxes animation
    const animateFeatureBadges = () => {
      const featureBadges = document.querySelectorAll('.feature-badge');
      const checkboxes = document.querySelectorAll('.feature-checkbox');

      // First animate the badges in
      setTimeout(() => {
        featureBadges.forEach((badge, index) => {
          setTimeout(() => {
            badge.classList.add('animate-in');
          }, index * 300);
        });

        // Then animate the checkboxes checking
        setTimeout(() => {
          checkboxes.forEach((checkbox, index) => {
            setTimeout(() => {
              checkbox.checked = true;
              checkbox.parentElement.parentElement.parentElement.classList.add('checked');
            }, index * 800);
          });
        }, 1000);
      }, 500);
    };

    // Smooth scrolling for navigation links
    const setupSmoothScrolling = () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
          const targetId = this.getAttribute('href');
          if (targetId === '#') return; // Skip if it's just "#"

          e.preventDefault();
          const targetElement = document.querySelector(targetId);

          if (targetElement) {
            window.scrollTo({
              top: targetElement.offsetTop - 80, // Adjust for navbar height
              behavior: 'smooth'
            });

            // Update active nav item
            document.querySelectorAll('.nav-link').forEach(navLink => {
              navLink.classList.remove('active');
            });
            this.classList.add('active');
          }
        });
      });
    };

    // Navbar behavior on scroll
    const setupNavbarScroll = () => {
      const navbar = document.querySelector('.navbar');
      if (!navbar) return;

      window.addEventListener('scroll', function () {
        if (window.scrollY > 30) {
          navbar.classList.add('shadow-sm', 'scrolled');
        } else {
          navbar.classList.remove('shadow-sm', 'scrolled');
        }
      });
    };

    // URL input handling
    const setupUrlInput = () => {
      const urlInput = document.querySelector('.url-input');
      const urlContainer = document.querySelector('.url-input-container');
      const placeholderText = document.querySelector('.placeholder-text');
      if (!urlInput || !urlContainer || !placeholderText) return;

      // Website examples for the placeholder
      const websiteExamples = [
        'yourwebsite.com',
        'whatsapp.com',
        'open-infotech.com',
        'google.com',
        'amazon.com',
        'netflix.com',
        'spotify.com'
      ];

      let currentExampleIndex = 0;

      // Function to update the placeholder text
      function updatePlaceholderText() {
        if (urlContainer.classList.contains('focused') || urlContainer.classList.contains('has-value')) {
          return;
        }

        placeholderText.style.animation = 'none';
        placeholderText.offsetHeight; // Trigger reflow
        placeholderText.textContent = websiteExamples[currentExampleIndex];
        placeholderText.style.animation = 'typing 3s steps(30) 1, blink-caret 0.75s step-end infinite';

        currentExampleIndex = (currentExampleIndex + 1) % websiteExamples.length;

        setTimeout(updatePlaceholderText, 4000); // Change every 4 seconds
      }

      // Start the placeholder cycling
      updatePlaceholderText();

      // Focus event
      urlInput.addEventListener('focus', function () {
        urlContainer.classList.add('focused');
        placeholderText.style.display = 'none';

        // Set cursor position at the end if there's already text
        if (this.value) {
          const length = this.value.length;
          this.setSelectionRange(length, length);
        }
      });

      // Blur event
      urlInput.addEventListener('blur', function () {
        if (!this.value) {
          urlContainer.classList.remove('focused');
          urlContainer.classList.remove('has-value');
          placeholderText.style.display = '';
        }
      });

      // Paste event - clean up pasted URLs
      urlInput.addEventListener('paste', function () {
        // Let the paste happen normally
        setTimeout(() => {
          let pastedText = this.value;

          // Remove http:// or https:// if present
          if (pastedText.startsWith('http://')) {
            this.value = pastedText.substring(7);
          } else if (pastedText.startsWith('https://')) {
            this.value = pastedText.substring(8);
          }

          if (this.value) {
            urlContainer.classList.add('has-value');
          } else {
            urlContainer.classList.remove('has-value');
          }
        }, 0);
      });

      // Input event
      urlInput.addEventListener('input', function () {
        if (this.value) {
          urlContainer.classList.add('has-value');
        } else {
          urlContainer.classList.remove('has-value');
        }
      });
    };

    // Feature options cycling is now handled by React state in Home.js
    const setupFeatureOptions = () => {
      // This function is intentionally empty as feature cycling is now handled by React state
      // in the Home.js component using useState and useEffect hooks
      return () => {}; // Return empty cleanup function
    };

    // Step items animation
    const setupStepItems = () => {
      const stepItems = document.querySelectorAll('.step-item');
      if (!stepItems.length) return;
      
      let activeStep = null;
      let activeStepIndex = 0;
      let stepAnimationInterval;

      // Function to cycle through steps
      function cycleSteps() {
        // Clear any existing interval
        if (stepAnimationInterval) {
          clearInterval(stepAnimationInterval);
        }

        // Make the first step active initially
        stepItems.forEach((item, index) => {
          if (index === 0) {
            item.classList.add('step-active', 'active');
            activeStep = item;
          } else {
            item.classList.remove('step-active', 'active');
          }
        });

        activeStepIndex = 0;

        // Set up interval to cycle through steps
        stepAnimationInterval = setInterval(() => {
          // Remove active class from current step
          stepItems[activeStepIndex].classList.remove('step-active', 'active');

          // Move to next step
          activeStepIndex = (activeStepIndex + 1) % stepItems.length;

          // Add active class to new step
          stepItems[activeStepIndex].classList.add('step-active', 'active');
          activeStep = stepItems[activeStepIndex];
        }, 4000);
      }

      // Start the animation when the page loads
      cycleSteps();

      // Add click event listeners to step items
      stepItems.forEach((item, index) => {
        item.addEventListener('click', function () {
          // Toggle active class on the clicked step item
          if (activeStep !== this) {
            // Remove active class from previously active step if any
            if (activeStep) {
              activeStep.classList.remove('step-active', 'active');
            }

            // Make the clicked step active
            this.classList.add('step-active', 'active');
            activeStep = this;
          }

          // Clear the animation interval
          clearInterval(stepAnimationInterval);
          stepAnimationInterval = null;

          // Update active index
          activeStepIndex = index;

          // Restart animation after 5 seconds of inactivity
          setTimeout(() => {
            if (!stepAnimationInterval) {
              cycleSteps();
            }
          }, 5000);
        });

        // Add cursor pointer to indicate it's clickable
        item.style.cursor = 'pointer';
      });
      
      // Clean up on unmount
      return () => {
        if (stepAnimationInterval) {
          clearInterval(stepAnimationInterval);
        }
      };
    };

    // Website URL demo form handling
    const setupDemoForm = () => {
      const demoForm = document.querySelector('.input-group.bg-white');
      if (!demoForm) return;

      const demoInput = demoForm.querySelector('input');
      const demoButton = demoForm.querySelector('button');

      const handleDemoSubmission = (url) => {
        if (!url) {
          showToast('Please enter a website URL');
          return;
        }

        // Validate URL format
        if (!url.match(/^(http(s)?:\/\/)?(www\.)?[a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/)) {
          showToast('Please enter a valid website URL');
          return;
        }

        // Show loading state
        const originalButtonText = demoButton.innerHTML;
        demoButton.innerHTML = '<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
        demoButton.disabled = true;

        // Simulate API call with timeout
        setTimeout(() => {
          demoButton.innerHTML = originalButtonText;
          demoButton.disabled = false;

          // Show success message
          showToast('Demo created! Check your email for access.', 'success');
          demoInput.value = '';
        }, 2000);
      };

      demoButton.addEventListener('click', function () {
        handleDemoSubmission(demoInput.value);
      });

      demoInput.addEventListener('keypress', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleDemoSubmission(demoInput.value);
        }
      });
    };

    // Scroll animations
    const setupScrollAnimations = () => {
      // Add animation on scroll
      const animateOnScroll = function () {
        const elements = document.querySelectorAll('.card, .feature-icon, .hero-image, .step-item');

        elements.forEach(element => {
          const elementPosition = element.getBoundingClientRect().top;
          const windowHeight = window.innerHeight;

          if (elementPosition < windowHeight - 50) {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
          }
        });

        // Special animation for demo section
        const demoSection = document.getElementById('demo');
        if (demoSection) {
          const demoPosition = demoSection.getBoundingClientRect().top;
          const windowHeight = window.innerHeight;
          
          if (demoPosition < windowHeight - 100) {
            // Animate arrow icons in the demo section
            const demoArrows = demoSection.querySelectorAll('.bi-arrow-right');
            demoArrows.forEach((arrow, index) => {
              setTimeout(() => {
                arrow.classList.add('animate-arrow');
              }, index * 200); // Stagger the animations
            });
            
            // Add a pulsing effect to buttons
            const demoButtons = demoSection.querySelectorAll('.btn-outline-gradient');
            demoButtons.forEach((button, index) => {
              setTimeout(() => {
                button.classList.add('pulse-effect');
              }, index * 300 + 500); // Stagger with delay after arrows
            });
            
            // Animate input boxes
            const inputBoxes = demoSection.querySelectorAll('input[type="text"], input[type="email"], input[type="url"]');
            inputBoxes.forEach((input, index) => {
              setTimeout(() => {
                input.classList.add('animate-input');
              }, index * 200 + 300);
            });
            
            // Animate checkboxes
            const checkboxes = demoSection.querySelectorAll('.feature-checkbox, .custom-checkbox');
            checkboxes.forEach((checkbox, index) => {
              setTimeout(() => {
                checkbox.classList.add('animate-checkbox');
                // If it's a real checkbox, check it
                if (checkbox.tagName === 'INPUT' && checkbox.type === 'checkbox') {
                  checkbox.checked = true;
                }
                // If it's a container with a checkbox inside
                const innerCheckbox = checkbox.querySelector('input[type="checkbox"]');
                if (innerCheckbox) {
                  innerCheckbox.checked = true;
                  const parentBadge = checkbox.closest('.feature-badge');
                  if (parentBadge) {
                    parentBadge.classList.add('checked');
                  }
                }
              }, index * 300 + 800); // Stagger with longer delay
            });
            
            // Add a class to the whole section to trigger other CSS animations
            demoSection.classList.add('animate-all');
          }
        }
      };

      // Initialize animations
      const initAnimations = function () {
        const elements = document.querySelectorAll('.card, .feature-icon, .hero-image, .step-item');
        elements.forEach(element => {
          element.style.opacity = '0';
          element.style.transform = 'translateY(20px)';
          element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        });

        // Run once on page load
        setTimeout(animateOnScroll, 300);
      };

      // Run animation check on scroll
      window.addEventListener('scroll', animateOnScroll);
      initAnimations();
      
      // Clean up on unmount
      return () => {
        window.removeEventListener('scroll', animateOnScroll);
      };
    };

    // Counters animation for metrics section
    const setupCounters = () => {
      // Check if element is in viewport
      const isInViewport = function (element) {
        const rect = element.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      const startCounters = function () {
        const counters = document.querySelectorAll('.metrics-section h3');
        const speed = 200; // The lower the faster

        counters.forEach(counter => {
          const target = counter.innerText;
          let suffix = '';

          // Extract suffix like K+, M+, X, etc.
          if (target.includes('+') || target.includes('X')) {
            suffix = target.match(/[KMB+X]+\+?/)[0];
          }

          // Extract the number part
          let targetNumber = parseInt(target.replace(/[^0-9]/g, ''));

          // Set starting point
          let count = 0;

          const updateCount = () => {
            const increment = targetNumber / speed;

            if (count < targetNumber) {
              count += increment;
              counter.innerText = Math.ceil(count) + suffix;
              setTimeout(updateCount, 1);
            } else {
              counter.innerText = targetNumber + suffix;
            }
          };

          // Store original text to avoid re-running
          counter.setAttribute('data-target', target);
          counter.innerText = '0' + suffix;

          updateCount();
        });
      };

      // Start counters when metrics section is in viewport
      let countersStarted = false;
      const scrollHandler = function () {
        const metricsSection = document.querySelector('.metrics-section');
        if (metricsSection && !countersStarted && isInViewport(metricsSection)) {
          startCounters();
          countersStarted = true;
        }
      };
      
      window.addEventListener('scroll', scrollHandler);
      
      // Clean up on unmount
      return () => {
        window.removeEventListener('scroll', scrollHandler);
      };
    };

    // Parallax effect for feature content
    const setupParallaxEffect = () => {
      const parallaxContainer = document.querySelector('.parallax-container');
      const parallaxLayers = document.querySelectorAll('.parallax-layer');
      
      if (!parallaxContainer || !parallaxLayers.length) return;
      
      // Track mouse position
      let mouseX = 0;
      let mouseY = 0;
      
      // Update mouse position on mouse move
      const mouseMoveHandler = (e) => {
        // Get mouse position relative to container
        const rect = parallaxContainer.getBoundingClientRect();
        mouseX = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 to 0.5
        mouseY = (e.clientY - rect.top) / rect.height - 0.5; // -0.5 to 0.5
        
        // Apply transform to each layer based on depth
        parallaxLayers.forEach(layer => {
          const depth = parseFloat(layer.getAttribute('data-depth')) || 0;
          const translateX = mouseX * depth * 100; // Adjust multiplier for effect intensity
          const translateY = mouseY * depth * 100;
          
          layer.style.transform = `translate(${translateX}px, ${translateY}px)`;
        });
      };
      
      // Reset position when mouse leaves
      const mouseLeaveHandler = () => {
        parallaxLayers.forEach(layer => {
          layer.style.transform = 'translate(0, 0)';
        });
      };
      
      parallaxContainer.addEventListener('mousemove', mouseMoveHandler);
      parallaxContainer.addEventListener('mouseleave', mouseLeaveHandler);
      
      // Clean up on unmount
      return () => {
        if (parallaxContainer) {
          parallaxContainer.removeEventListener('mousemove', mouseMoveHandler);
          parallaxContainer.removeEventListener('mouseleave', mouseLeaveHandler);
        }
      };
    };

    // Toast notification system
    window.showToast = function(message, type = 'warning') {
      // Create toast container if it doesn't exist
      let toastContainer = document.querySelector('.toast-container');
      if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.className = 'toast-container position-fixed bottom-0 end-0 p-3';
        document.body.appendChild(toastContainer);
      }

      // Create toast element
      const toastEl = document.createElement('div');
      toastEl.className = `toast align-items-center text-white bg-${type} border-0`;
      toastEl.setAttribute('role', 'alert');
      toastEl.setAttribute('aria-live', 'assertive');
      toastEl.setAttribute('aria-atomic', 'true');

      // Toast content
      toastEl.innerHTML = `
          <div className="d-flex">
              <div className="toast-body">
                  ${message}
              </div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
          </div>
      `;

      // Add to container
      toastContainer.appendChild(toastEl);

      // Initialize and show toast
      const toast = new bootstrap.Toast(toastEl, {
        autohide: true,
        delay: 3000
      });
      toast.show();

      // Remove toast after it's hidden
      toastEl.addEventListener('hidden.bs.toast', function () {
        toastEl.remove();
      });
    };

    // Initialize all the interactions
    animateFeatureBadges();
    setupSmoothScrolling();
    setupNavbarScroll();
    setupUrlInput();
    const cleanupFeatureOptions = setupFeatureOptions();
    const cleanupStepItems = setupStepItems();
    setupDemoForm();
    const cleanupScrollAnimations = setupScrollAnimations();
    const cleanupCounters = setupCounters();
    const cleanupParallax = setupParallaxEffect();

    // Return cleanup function
    return () => {
      if (cleanupFeatureOptions) cleanupFeatureOptions();
      if (cleanupStepItems) cleanupStepItems();
      if (cleanupScrollAnimations) cleanupScrollAnimations();
      if (cleanupCounters) cleanupCounters();
      if (cleanupParallax) cleanupParallax();
    };
  }, []);
};

export default useHomeInteractions;
